import { type ApiResponse } from "../types/api/ApiResponse";
import Config from "../Config";
import { getSessionId } from "../utilities/sessionContext";

const isSsr = import.meta.env.SSR;

if (isSsr && Config.isDev) {
	process.env.NODE_TLS_REJECT_UNAUTHORIZED = "0";
}

async function ApiRequest(url: string, method: "GET" | "POST" | "PUT" | "DELETE", data: any = null): Promise<ApiResponse> {
	const { apiUrl, apiVersion } = Config;

	let signal: AbortSignal | undefined = undefined;
	if (typeof data?.signal !== "undefined") {
		if (data.signal) {
			signal = data.signal;
		}
		delete data.signal;
	}

	// Build request body for POST requests
	let body = null;
	if (method === "POST" && data) {
		const FormDataCtor = typeof FormData !== "undefined" ? FormData : (await import("form-data")).default;
		body = new FormDataCtor();
		Object.entries(data).forEach(([key, value]) => {
			if (typeof value === "object" && !Array.isArray(value)) {
				Object.entries(value).forEach(([nestedKey, nestedValue]) => {
					body.append(`${key}[${nestedKey}]`, nestedValue);
				});
			} else if (Array.isArray(value)) {
				value.forEach((v) => body.append(`${key}[]`, v));
			} else {
				body.append(key, value);
			}
		});
	}

	// Get session ID from context (works on both server and client)
	const sessionId = getSessionId();

	// Build headers with session ID
	const headers: Record<string, string> = {};
	if (sessionId) {
		headers["X-Session-Id"] = sessionId;
	}

	// Build fetch options
	const fetchOptions: RequestInit = {
		method,
		credentials: "include", // equivalent to withCredentials: true
		headers,
	};

	if (body) {
		fetchOptions.body = body;
	}

	if (signal) {
		fetchOptions.signal = signal;
	}

	try {
		const response = await fetch(`${apiUrl}/${apiVersion}/${url}`, fetchOptions);

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const responseData = await response.json() as ApiResponse;

		// Handle API-level errors (success: false)
		if (responseData.success === false) {
			throw new Error(responseData.errors.join(" "));
		}

		// If the API returned errors array, throw it
		if (responseData?.errors?.length > 0) {
			throw new Error(responseData.errors.join(","));
		}

		if (responseData.response) {
			return { ...responseData.response };
		} else {
			throw new Error("API response is missing 'response' field");
		}
	} catch (error) {
		// Throw it again so we can catch it elsewhere
		throw error;
	}
}

async function WithCache<T>(env: Env, cacheKey: string, ttlSeconds: number, fn: () => Promise<T>): Promise<T> {
	const cachedResponse = await env.CACHE.get(cacheKey);

	if (cachedResponse) {
		return JSON.parse(cachedResponse);
	}

	const response = await fn();
	await env.CACHE.put(cacheKey, JSON.stringify(response), { expirationTtl: ttlSeconds });
	return response;
}

export { ApiRequest, WithCache };
